// src/pages/ResultsPublic/ResultsPublicPage.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import {
  getAllCompetitions,
  getCompetitionById,
} from "../../services/competitionsServices";
import { getAllPartenaires } from "../../services/partenairesServices";

import useScrollToTop from "../../hooks/useScrollToTop";
import ScrollIndicator from "../../hooks/ScrollIndicator";
import { useUser } from "../../contexts/UserContext";

import CompetitionPartnersSection from "./CompetitionPartnersSection";

import {
  FaArrowRight,
  FaCalendarAlt,
  FaClock,
  FaExternalLinkAlt,
  FaFilter,
  FaImage,
  FaInfoCircle,
  FaMapMarkerAlt,
  FaSearch,
  FaShareAlt,
  FaTimes,
  FaTrophy,
} from "react-icons/fa";

/* ----------------------------- helpers ----------------------------- */

const PAGE =
  "min-h-screen bg-slate-50 text-slate-900 dark:bg-slate-950 dark:text-slate-50";

const INPUT =
  "w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-800 outline-none transition placeholder:text-slate-400 focus:border-indigo-400 focus:ring-4 focus:ring-indigo-100 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100 dark:focus:border-indigo-500 dark:focus:ring-indigo-950";

const useDebounced = (value, delay = 300) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);

  return debouncedValue;
};

const normalizeText = (value) =>
  String(value || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "");

const safeArray = (maybeJSON, fallback = []) => {
  try {
    if (!maybeJSON) return fallback;

    if (Array.isArray(maybeJSON)) {
      return maybeJSON.filter(Boolean);
    }

    if (typeof maybeJSON === "string") {
      const trimmed = maybeJSON.trim();
      if (!trimmed) return fallback;

      try {
        const parsed = JSON.parse(trimmed);

        if (Array.isArray(parsed)) return parsed.filter(Boolean);
        if (typeof parsed === "string") return [parsed].filter(Boolean);
      } catch {
        // fallback string simple
      }

      if (trimmed.includes(",")) {
        return trimmed
          .split(",")
          .map((item) => item.trim())
          .filter(Boolean);
      }

      return [trimmed];
    }

    return fallback;
  } catch {
    return fallback;
  }
};

const toCompetitionDate = (value) => {
  if (!value) return null;

  const raw = String(value).trim();
  const normalized = raw.includes(" ") ? raw.replace(" ", "T") : raw;
  const date = new Date(normalized);

  return Number.isNaN(date.getTime()) ? null : date;
};

const todayDateOnly = () => {
  const today = new Date();
  return new Date(today.getFullYear(), today.getMonth(), today.getDate());
};

const toDateOnly = (value) => {
  const date = toCompetitionDate(value);
  if (!date) return null;

  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
};

const formatDateShort = (value) => {
  const date = toCompetitionDate(value);
  if (!date) return "Date non renseignée";

  return new Intl.DateTimeFormat("fr-FR", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  }).format(date);
};

const formatDateLong = (value) => {
  const date = toCompetitionDate(value);
  if (!date) return "Date non renseignée";

  return new Intl.DateTimeFormat("fr-FR", {
    weekday: "long",
    day: "2-digit",
    month: "long",
    year: "numeric",
  }).format(date);
};

const getTemporalStatus = (competition) => {
  const date = toDateOnly(competition?.date_competition);
  const today = todayDateOnly();

  if (!date) {
    return {
      label: "Date inconnue",
      tone: "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200",
    };
  }

  if (date < today) {
    return {
      label: "Passée",
      tone: "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200",
    };
  }

  if (date.getTime() === today.getTime()) {
    return {
      label: "Aujourd’hui",
      tone:
        "bg-amber-100 text-amber-700 dark:bg-amber-950/70 dark:text-amber-300",
    };
  }

  return {
    label: "À venir",
    tone:
      "bg-emerald-100 text-emerald-700 dark:bg-emerald-950/70 dark:text-emerald-300",
  };
};

const stripHtmlToText = (html) => {
  if (!html) return "";

  return String(html)
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<\/p>/gi, "\n")
    .replace(/<\/li>/gi, "\n")
    .replace(/<li>/gi, "• ")
    .replace(/<[^>]*>/g, "")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .trim();
};

const extractOrganisateurUserId = (competition) => {
  if (!competition) return null;

  const direct = competition.user_id ?? competition.organisateur_user_id;

  if (direct !== undefined && direct !== null && String(direct).trim() !== "") {
    const numeric = Number(direct);
    return Number.isFinite(numeric) ? numeric : null;
  }

  const value = competition.organisateur_competition;

  if (typeof value === "number") return value;

  if (typeof value === "string") {
    const trimmed = value.trim();

    if (!trimmed) return null;
    if (/^\d+$/.test(trimmed)) return Number(trimmed);

    try {
      const parsed = JSON.parse(trimmed);
      const candidate = parsed?.user_id ?? parsed?.userId ?? parsed?.id;
      const numeric = Number(candidate);

      return Number.isFinite(numeric) ? numeric : null;
    } catch {
      return null;
    }
  }

  if (typeof value === "object" && value) {
    const candidate = value.user_id ?? value.userId ?? value.id;
    const numeric = Number(candidate);

    return Number.isFinite(numeric) ? numeric : null;
  }

  return null;
};

/* ----------------------------- main page ----------------------------- */

export default function ResultsPublicPage() {
  useScrollToTop();

  const navigate = useNavigate();
  const { user } = useUser();

  const isSuperAdmin = user && Number(user.userId) === 1;

  const [competitions, setCompetitions] = useState([]);
  const [loading, setLoading] = useState(true);

  const [selectedCompetition, setSelectedCompetition] = useState(null);
  const [selectedCompetitionFull, setSelectedCompetitionFull] = useState(null);

  const [partenaires, setPartenaires] = useState([]);
  const [loadingPartenaires, setLoadingPartenaires] = useState(false);

  const [enlargedImage, setEnlargedImage] = useState(null);

  const [search, setSearch] = useState("");
  const [level, setLevel] = useState("all");
  const [period, setPeriod] = useState("todayup");
  const [sortBy, setSortBy] = useState("date_desc");

  const debouncedSearch = useDebounced(search, 300);

  useEffect(() => {
    let cancelled = false;

    const fetchCompetitions = async () => {
      try {
        setLoading(true);

        const data = await getAllCompetitions();

        if (!cancelled) {
          setCompetitions(Array.isArray(data) ? data : []);
        }
      } catch (error) {
        console.error("Erreur lors du chargement des compétitions :", error);
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchCompetitions();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    const loadDetailsAndPartners = async () => {
      if (!selectedCompetition?.id) {
        setSelectedCompetitionFull(null);
        setPartenaires([]);
        return;
      }

      setLoadingPartenaires(true);
      setSelectedCompetitionFull(null);

      try {
        const fullCompetition = await getCompetitionById(selectedCompetition.id);

        if (cancelled) return;

        setSelectedCompetitionFull(fullCompetition || selectedCompetition);

        const organisateurUserId = extractOrganisateurUserId(
          fullCompetition || selectedCompetition
        );

        if (!organisateurUserId) {
          setPartenaires([]);
          return;
        }

        const allPartners = await getAllPartenaires();

        if (cancelled) return;

        const list = (Array.isArray(allPartners) ? allPartners : [])
          .filter((partner) => Number(partner.user_id) === Number(organisateurUserId))
          .filter((partner) => partner.is_active === true || partner.is_active === 1)
          .sort((a, b) => (a.ordre ?? 9999) - (b.ordre ?? 9999));

        setPartenaires(list);
      } catch (error) {
        console.error("Erreur chargement détail compétition / partenaires :", error);
        setSelectedCompetitionFull(selectedCompetition);
        setPartenaires([]);
      } finally {
        if (!cancelled) {
          setLoadingPartenaires(false);
        }
      }
    };

    loadDetailsAndPartners();

    return () => {
      cancelled = true;
    };
  }, [selectedCompetition]);

  const visibleCompetitions = useMemo(() => {
    if (isSuperAdmin) return competitions;

    return competitions.filter(
      (competition) =>
        competition.is_public === true &&
        competition.is_active === true
    );
  }, [competitions, isSuperAdmin]);

  const levels = useMemo(() => {
    const values = new Set(
      visibleCompetitions
        .map((competition) => competition.level_competition)
        .filter((value) => value != null && String(value).trim() !== "")
    );

    return ["all", ...Array.from(values).sort((a, b) => String(a).localeCompare(String(b), "fr"))];
  }, [visibleCompetitions]);

  const filtered = useMemo(() => {
    const today = todayDateOnly();
    const query = normalizeText(debouncedSearch.trim());

    let rows = [...visibleCompetitions];

    if (query) {
      rows = rows.filter((competition) => {
        const disciplines = safeArray(competition.disciplines_competition).join(" ");

        const searchable = [
          competition.nom_competition,
          competition.lieu_competition,
          competition.level_competition,
          disciplines,
        ]
          .map(normalizeText)
          .join(" ");

        return searchable.includes(query);
      });
    }

    if (level !== "all") {
      rows = rows.filter(
        (competition) => String(competition.level_competition) === String(level)
      );
    }

    if (period !== "all") {
      rows = rows.filter((competition) => {
        const date = toDateOnly(competition.date_competition);
        if (!date) return false;

        if (period === "past") return date < today;
        if (period === "todayup") return date >= today;
        if (period === "upcoming") return date > today;

        return true;
      });
    }

    const byDate = (competition) =>
      toCompetitionDate(competition.date_competition)?.getTime() || 0;

    if (sortBy === "date_asc") {
      rows.sort((a, b) => byDate(a) - byDate(b));
    } else if (sortBy === "name_asc") {
      rows.sort((a, b) =>
        String(a.nom_competition || "").localeCompare(
          String(b.nom_competition || ""),
          "fr"
        )
      );
    } else {
      rows.sort((a, b) => byDate(b) - byDate(a));
    }

    return rows;
  }, [visibleCompetitions, debouncedSearch, level, period, sortBy]);

  const stats = useMemo(() => {
    const today = todayDateOnly();

    let past = 0;
    let todayCount = 0;
    let upcoming = 0;

    for (const competition of visibleCompetitions) {
      const date = toDateOnly(competition.date_competition);

      if (!date) continue;

      if (date < today) past += 1;
      if (date.getTime() === today.getTime()) todayCount += 1;
      if (date > today) upcoming += 1;
    }

    return {
      total: visibleCompetitions.length,
      filtered: filtered.length,
      past,
      today: todayCount,
      upcoming,
    };
  }, [visibleCompetitions, filtered]);

  const drawerCompetition = selectedCompetitionFull || selectedCompetition;

  const handleShare = async (competition) => {
    if (!competition?.id) return;

    const url = `${window.location.origin}/resultats-public/${competition.id}`;
    const title = `Résultats — ${competition.nom_competition}`;
    const text = `${competition.nom_competition || "Compétition"} • ${
      competition.lieu_competition || ""
    } • ${competition.level_competition || ""}`;

    try {
      if (navigator.share) {
        await navigator.share({ title, text, url });
        return;
      }

      await navigator.clipboard.writeText(url);
      alert("Lien copié dans le presse-papiers.");
    } catch {
      window.prompt("Copier le lien :", url);
    }
  };

  const handleNavigate = (id) => {
    if (!id) return;
    navigate(`/resultats-public/${id}`);
  };

  return (
    <div className={PAGE}>
      <ScrollIndicator />

      <main className="mx-auto w-full max-w-[1280px] px-3 pb-12 pt-[120px] sm:px-5 md:pt-[145px]">
        <section className="overflow-hidden rounded-[32px] bg-slate-950 shadow-xl dark:bg-slate-900">
          <div className="relative p-5 sm:p-7 lg:p-8">
            <div className="absolute -right-16 -top-16 h-52 w-52 rounded-full bg-indigo-500/25 blur-3xl" />
            <div className="absolute bottom-0 left-1/3 h-40 w-40 rounded-full bg-pink-500/20 blur-3xl" />
            <div className="absolute -bottom-20 -left-20 h-56 w-56 rounded-full bg-blue-500/15 blur-3xl" />

            <div className="relative">
              <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-3 py-1.5 text-xs font-bold uppercase tracking-[0.18em] text-white/80">
                <FaTrophy />
                Résultats publics
              </div>

              <h1 className="text-3xl font-bold tracking-tight text-white sm:text-4xl">
                Résultats des compétitions
              </h1>

              <p className="mt-2 max-w-2xl text-sm leading-relaxed text-slate-300 sm:text-base">
                Sélectionnez une compétition pour consulter ses informations,
                ses partenaires et accéder rapidement aux résultats.
              </p>

              {isSuperAdmin ? (
                <div className="mt-4 inline-flex rounded-2xl bg-amber-400/15 px-4 py-2 text-xs font-bold text-amber-200 ring-1 ring-amber-300/20">
                  Mode administrateur : les compétitions non publiques sont visibles.
                </div>
              ) : null}
            </div>

            <div className="relative mt-7 grid grid-cols-2 gap-3 lg:grid-cols-4">
              <HeroStat label="Compétitions" value={stats.total} />
              <HeroStat label="Affichées" value={stats.filtered} />
              <HeroStat label="Aujourd’hui" value={stats.today} />
              <HeroStat label="À venir" value={stats.upcoming} />
            </div>
          </div>
        </section>

        <section className="sticky top-3 z-10 mt-6 rounded-[28px] border border-slate-200 bg-white/95 p-4 shadow-sm backdrop-blur dark:border-slate-800 dark:bg-slate-900/95 sm:p-5">
          <div className="mb-4 flex items-center gap-2">
            <div className="inline-flex items-center gap-2 rounded-full bg-indigo-50 px-3 py-1 text-xs font-bold uppercase tracking-wide text-indigo-700 dark:bg-indigo-950/70 dark:text-indigo-300">
              <FaFilter />
              Filtres
            </div>

            <span className="text-sm font-semibold text-slate-500 dark:text-slate-400">
              {filtered.length} résultat{filtered.length > 1 ? "s" : ""}
            </span>
          </div>

          <div className="grid grid-cols-1 gap-3 lg:grid-cols-12">
            <div className="relative lg:col-span-5">
              <FaSearch className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="search"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Rechercher par nom, lieu, discipline..."
                className={`${INPUT} pl-11`}
              />
            </div>

            <select
              value={level}
              onChange={(event) => setLevel(event.target.value)}
              className={`${INPUT} lg:col-span-2`}
            >
              {levels.map((item) => (
                <option key={item} value={item}>
                  {item === "all" ? "Tous niveaux" : item}
                </option>
              ))}
            </select>

            <select
              value={period}
              onChange={(event) => setPeriod(event.target.value)}
              className={`${INPUT} lg:col-span-2`}
            >
              <option value="all">Toutes périodes</option>
              <option value="todayup">Aujourd’hui & à venir</option>
              <option value="upcoming">À venir</option>
              <option value="past">Passées</option>
            </select>

            <select
              value={sortBy}
              onChange={(event) => setSortBy(event.target.value)}
              className={`${INPUT} lg:col-span-3`}
            >
              <option value="date_desc">Plus récentes</option>
              <option value="date_asc">Plus anciennes</option>
              <option value="name_asc">Nom A → Z</option>
            </select>
          </div>
        </section>

        <section className="mt-6">
          {loading ? (
            <LoadingGrid />
          ) : filtered.length === 0 ? (
            <EmptyState />
          ) : (
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
              {filtered.map((competition) => (
                <CompetitionListCard
                  key={competition.id}
                  competition={competition}
                  isSelected={selectedCompetition?.id === competition.id}
                  onOpen={() => setSelectedCompetition(competition)}
                  onResults={() => handleNavigate(competition.id)}
                />
              ))}
            </div>
          )}
        </section>
      </main>

      <CompetitionDetailsDrawer
        competition={drawerCompetition}
        partenaires={partenaires}
        loadingPartenaires={loadingPartenaires}
        onClose={() => {
          setSelectedCompetition(null);
          setSelectedCompetitionFull(null);
          setPartenaires([]);
        }}
        onNavigate={handleNavigate}
        onShare={handleShare}
        onImageClick={setEnlargedImage}
      />

      {enlargedImage ? (
        <div
          className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/85 p-4"
          onClick={() => setEnlargedImage(null)}
          role="dialog"
          aria-modal="true"
        >
          <button
            type="button"
            onClick={() => setEnlargedImage(null)}
            className="absolute right-4 top-4 inline-flex h-11 w-11 items-center justify-center rounded-2xl bg-white/10 text-white backdrop-blur transition hover:bg-white/20"
            aria-label="Fermer l’image"
          >
            <FaTimes />
          </button>

          <img
            src={enlargedImage}
            alt="Agrandissement compétition"
            className="max-h-[90vh] max-w-5xl rounded-3xl object-contain shadow-2xl"
          />
        </div>
      ) : null}
    </div>
  );
}

/* ----------------------------- components ----------------------------- */

function HeroStat({ label, value }) {
  return (
    <div className="rounded-[24px] border border-white/10 bg-white/[0.08] p-4 text-white shadow-sm backdrop-blur">
      <div className="text-xs font-bold uppercase tracking-wide text-white/60">
        {label}
      </div>
      <div className="mt-1 text-3xl font-bold">{value}</div>
    </div>
  );
}

function CompetitionListCard({ competition, isSelected, onOpen, onResults }) {
  const photo = competition.photo_URL || "/img/competition_default.jpg";
  const disciplines = safeArray(competition.disciplines_competition);
  const temporalStatus = getTemporalStatus(competition);

  return (
    <article
      className={`group overflow-hidden rounded-[28px] border bg-white shadow-sm transition hover:-translate-y-1 hover:shadow-xl dark:bg-slate-900 ${
        isSelected
          ? "border-indigo-400 ring-4 ring-indigo-100 dark:border-indigo-500 dark:ring-indigo-950"
          : "border-slate-200 dark:border-slate-800"
      }`}
    >
      <button
        type="button"
        onClick={onOpen}
        className="block w-full text-left"
      >
        <div className="relative h-48 overflow-hidden bg-slate-100 dark:bg-slate-800">
          <img
            src={photo}
            alt={competition.nom_competition || "Compétition"}
            onError={(event) => {
              event.currentTarget.src = "/img/competition_default.jpg";
            }}
            className="h-full w-full object-cover transition duration-500 group-hover:scale-105"
            loading="lazy"
          />

          <div className="absolute inset-0 bg-gradient-to-t from-slate-950/75 via-slate-950/10 to-transparent" />

          <div className="absolute left-3 top-3 flex flex-wrap gap-2">
            <Badge className={temporalStatus.tone}>
              {temporalStatus.label}
            </Badge>

            {competition.level_competition ? (
              <Badge className="bg-white/90 text-slate-800 backdrop-blur">
                {competition.level_competition}
              </Badge>
            ) : null}
          </div>

          <div className="absolute bottom-3 left-3 right-3">
            <h2 className="line-clamp-2 text-lg font-bold leading-tight text-white">
              {competition.nom_competition || "Compétition sans nom"}
            </h2>
          </div>
        </div>

        <div className="space-y-3 p-4">
          <InfoLine
            icon={FaCalendarAlt}
            value={formatDateShort(competition.date_competition)}
          />

          <InfoLine
            icon={FaMapMarkerAlt}
            value={competition.lieu_competition || "Lieu non renseigné"}
          />

          <div className="flex flex-wrap gap-1.5">
            {disciplines.slice(0, 3).map((discipline, index) => (
              <span
                key={`${discipline}-${index}`}
                className="rounded-full bg-slate-100 px-3 py-1 text-xs font-bold text-slate-700 dark:bg-slate-800 dark:text-slate-200"
              >
                {discipline}
              </span>
            ))}

            {disciplines.length > 3 ? (
              <span className="rounded-full bg-slate-200 px-3 py-1 text-xs font-bold text-slate-600 dark:bg-slate-700 dark:text-slate-200">
                +{disciplines.length - 3}
              </span>
            ) : null}
          </div>
        </div>
      </button>

      <div className="grid grid-cols-2 gap-2 border-t border-slate-100 p-4 dark:border-slate-800">
        <button
          type="button"
          onClick={onOpen}
          className="inline-flex items-center justify-center gap-2 rounded-2xl bg-slate-100 px-3 py-2.5 text-sm font-bold text-slate-700 transition hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-100 dark:hover:bg-slate-700"
        >
          <FaInfoCircle />
          Détail
        </button>

        <button
          type="button"
          onClick={onResults}
          className="inline-flex items-center justify-center gap-2 rounded-2xl bg-indigo-600 px-3 py-2.5 text-sm font-bold text-white transition hover:bg-indigo-700"
        >
          Résultats
          <FaArrowRight />
        </button>
      </div>
    </article>
  );
}

function CompetitionDetailsDrawer({
  competition,
  partenaires,
  loadingPartenaires,
  onClose,
  onNavigate,
  onShare,
  onImageClick,
}) {
  useEffect(() => {
    if (!competition) return;

    const root = document.documentElement;
    const previousOverflow = root.style.overflow;

    root.style.overflow = "hidden";

    const onEscape = (event) => {
      if (event.key === "Escape") onClose?.();
    };

    window.addEventListener("keydown", onEscape);

    return () => {
      root.style.overflow = previousOverflow;
      window.removeEventListener("keydown", onEscape);
    };
  }, [competition, onClose]);

  if (!competition) return null;

  const photo = competition.photo_URL || "/img/competition_default.jpg";
  const disciplines = safeArray(competition.disciplines_competition);
  const temporalStatus = getTemporalStatus(competition);
  const horaires = stripHtmlToText(competition.horaires_pesees);

  return (
    <div className="fixed inset-0 z-[9999]">
      <button
        type="button"
        aria-label="Fermer"
        onClick={onClose}
        className="absolute inset-0 bg-slate-950/60 backdrop-blur-sm"
      />

      <aside className="absolute right-0 top-0 flex h-full w-full max-w-2xl flex-col overflow-hidden bg-white shadow-2xl dark:bg-slate-950">
        <div className="relative h-64 shrink-0 overflow-hidden bg-slate-900">
          <img
            src={photo}
            alt={competition.nom_competition || "Compétition"}
            onError={(event) => {
              event.currentTarget.src = "/img/competition_default.jpg";
            }}
            className="h-full w-full object-cover opacity-80"
          />

          <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/35 to-transparent" />

          <button
            type="button"
            onClick={onClose}
            className="absolute right-4 top-4 inline-flex h-10 w-10 items-center justify-center rounded-2xl bg-white/15 text-white backdrop-blur transition hover:bg-white/25"
            aria-label="Fermer le détail"
          >
            <FaTimes />
          </button>

          <button
            type="button"
            onClick={() => onImageClick(photo)}
            className="absolute left-4 top-4 inline-flex items-center gap-2 rounded-2xl bg-white/15 px-3 py-2 text-xs font-bold text-white backdrop-blur transition hover:bg-white/25"
          >
            <FaImage />
            Agrandir
          </button>

          <div className="absolute bottom-4 left-4 right-4">
            <div className="mb-3 flex flex-wrap gap-2">
              <Badge className={temporalStatus.tone}>{temporalStatus.label}</Badge>

              {competition.is_active !== undefined ? (
                <Badge
                  className={
                    competition.is_active
                      ? "bg-emerald-100 text-emerald-700"
                      : "bg-red-100 text-red-700"
                  }
                >
                  {competition.is_active ? "Active" : "Inactive"}
                </Badge>
              ) : null}

              {competition.is_public !== undefined ? (
                <Badge
                  className={
                    competition.is_public
                      ? "bg-blue-100 text-blue-700"
                      : "bg-amber-100 text-amber-700"
                  }
                >
                  {competition.is_public ? "Publique" : "Non publique"}
                </Badge>
              ) : null}
            </div>

            <h2 className="text-2xl font-bold leading-tight text-white sm:text-3xl">
              {competition.nom_competition || "Compétition sans nom"}
            </h2>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-4 sm:p-6">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <DetailCard
              icon={FaCalendarAlt}
              label="Date"
              value={formatDateLong(competition.date_competition)}
            />

            <DetailCard
              icon={FaMapMarkerAlt}
              label="Lieu"
              value={competition.lieu_competition || "Lieu non renseigné"}
            />

            <DetailCard
              icon={FaTrophy}
              label="Niveau"
              value={competition.level_competition || "Niveau non renseigné"}
            />
          </div>

          <section className="mt-4 rounded-[24px] border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-900">
            <h3 className="text-sm font-bold uppercase tracking-wide text-slate-400">
              Disciplines
            </h3>

            <div className="mt-3 flex flex-wrap gap-2">
              {disciplines.length > 0 ? (
                disciplines.map((discipline, index) => (
                  <span
                    key={`${discipline}-${index}`}
                    className="rounded-full bg-slate-100 px-3 py-1.5 text-xs font-bold text-slate-700 dark:bg-slate-800 dark:text-slate-200"
                  >
                    {discipline}
                  </span>
                ))
              ) : (
                <span className="text-sm text-slate-500">
                  Aucune discipline renseignée.
                </span>
              )}
            </div>
          </section>

          <section className="mt-4 rounded-[24px] border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-900">
            <h3 className="flex items-center gap-2 text-sm font-bold uppercase tracking-wide text-slate-400">
              <FaClock />
              Horaires de pesées
            </h3>

            <p className="mt-3 whitespace-pre-wrap text-sm leading-relaxed text-slate-700 dark:text-slate-200">
              {horaires || "Aucun horaire renseigné."}
            </p>
          </section>

          <div className="mt-4">
            <CompetitionPartnersSection
              partenaires={partenaires}
              loading={loadingPartenaires}
            />
          </div>
        </div>

        <div className="shrink-0 border-t border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-950 sm:p-5">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <button
              type="button"
              onClick={() => onShare(competition)}
              className="inline-flex items-center justify-center gap-2 rounded-2xl bg-slate-100 px-4 py-3 text-sm font-bold text-slate-700 transition hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-100 dark:hover:bg-slate-700"
            >
              <FaShareAlt />
              Partager
            </button>

            <button
              type="button"
              onClick={() => {
                const url = `${window.location.origin}/resultats-public/${competition.id}`;
                window.open(url, "_blank", "noopener,noreferrer");
              }}
              className="inline-flex items-center justify-center gap-2 rounded-2xl bg-white px-4 py-3 text-sm font-bold text-slate-800 ring-1 ring-slate-200 transition hover:bg-slate-50 dark:bg-slate-900 dark:text-slate-100 dark:ring-slate-800 dark:hover:bg-slate-800"
            >
              <FaExternalLinkAlt />
              Nouvel onglet
            </button>

            <button
              type="button"
              onClick={() => onNavigate(competition.id)}
              className="inline-flex items-center justify-center gap-2 rounded-2xl bg-indigo-600 px-4 py-3 text-sm font-bold text-white transition hover:bg-indigo-700"
            >
              Voir les résultats
              <FaArrowRight />
            </button>
          </div>
        </div>
      </aside>
    </div>
  );
}

function DetailCard({ icon: Icon, label, value }) {
  return (
    <div className="rounded-[24px] border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-900">
      <div className="flex items-start gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-indigo-50 text-indigo-600 dark:bg-indigo-950/70 dark:text-indigo-300">
          <Icon />
        </div>

        <div>
          <div className="text-xs font-bold uppercase tracking-wide text-slate-400">
            {label}
          </div>

          <div className="mt-1 font-bold text-slate-900 dark:text-white">
            {value || "—"}
          </div>
        </div>
      </div>
    </div>
  );
}

function InfoLine({ icon: Icon, value }) {
  return (
    <div className="flex items-center gap-2 text-sm font-semibold text-slate-600 dark:text-slate-300">
      <Icon className="shrink-0 text-slate-400" />
      <span className="truncate">{value}</span>
    </div>
  );
}

function Badge({ children, className }) {
  return (
    <span
      className={`inline-flex rounded-full px-3 py-1.5 text-xs font-bold ${className}`}
    >
      {children}
    </span>
  );
}

function LoadingGrid() {
  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
      {Array.from({ length: 9 }).map((_, index) => (
        <div
          key={index}
          className="overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900"
        >
          <div className="h-48 animate-pulse bg-slate-200 dark:bg-slate-800" />
          <div className="space-y-3 p-4">
            <div className="h-4 w-3/4 animate-pulse rounded-full bg-slate-200 dark:bg-slate-800" />
            <div className="h-4 w-1/2 animate-pulse rounded-full bg-slate-200 dark:bg-slate-800" />
            <div className="h-4 w-2/3 animate-pulse rounded-full bg-slate-200 dark:bg-slate-800" />
          </div>
        </div>
      ))}
    </div>
  );
}

function EmptyState() {
  return (
    <div className="flex flex-col items-center justify-center rounded-[28px] border border-dashed border-slate-300 bg-white px-5 py-16 text-center dark:border-slate-700 dark:bg-slate-900">
      <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-[24px] bg-slate-100 text-2xl text-slate-500 dark:bg-slate-800 dark:text-slate-300">
        <FaTrophy />
      </div>

      <h3 className="text-lg font-bold text-slate-900 dark:text-white">
        Aucune compétition disponible
      </h3>

      <p className="mt-2 max-w-md text-sm leading-relaxed text-slate-500 dark:text-slate-400">
        Essayez de modifier votre recherche, le niveau ou la période sélectionnée.
      </p>
    </div>
  );
}